<template>
  <el-form
    :label-width="labelWidth"
    :model="fM"
    :rules="fR"
    ref="rFormRef"
  >
    <el-row>
      <template
        v-for="(item, index) in fS"
        :key="index"
      >
        <!-- 独占一行的项目 -->
        <el-col
          v-if="item.fullRow"
          :span="itemSpan * 2"
          :offset="itemOffset"
        >
          <el-form-item
            :label="t(item.label)"
            :prop="item.prop"
          >
            <!-- 表单控件 -->
            <FormControl
              :item="item"
              :value="fM[item.prop]"
              @update:value="(val) => fM[item.prop] = val"
            />
            <!-- 插槽 -->
            <div
              v-if="$slots[`after-${item.prop}`]"
              class="full-row-slot"
            >
              <slot
                :name="`after-${item.prop}`"
                :item="item"
                :value="fM[item.prop]"
              />
            </div>
          </el-form-item>
        </el-col>

        <!-- 正常行项目 -->
        <template v-else>
          <el-col
            :span="itemSpan"
            :offset="itemOffset"
          >
            <el-form-item
              v-if="item.isSlot"
              :label="item.label"
              :prop="item.prop"
            >
              <slot
                :name="`slot-${item.prop}`"
                :item="item"
                :formModel="fM"
              />
            </el-form-item>
            <el-form-item
              v-else
              :label="item.label"
              :prop="item.prop"
            >
              <FormControl
                :item="item"
                :value="fM[item.prop]"
                @update:value="(val) => fM[item.prop] = val"
              />
            </el-form-item>
          </el-col>
          <!-- 正常行的插槽列 -->
          <el-col
            v-if="$slots[`after-${item.prop}`] && hadSlot"
            :span="slotSpan"
          >
            <div class="button-slot-wrapper">
              <slot
                :name="`after-${item.prop}`"
                :item="item"
                :value="fM[item.prop]"
              />
            </div>
          </el-col>
          <el-col
            v-else-if="hadSlot"
            :span="slotSpan"
          />
        </template>
      </template>
    </el-row>
    <div
      v-if="!readonly"
      style="width:100%;display:flex;justify-content: center;"
    >
      <el-button
        type="primary"
        :disabled="submitDisabled"
        @click="submitEmit"
      >
        {{ t(submitButton) }}
      </el-button>
      <el-button
        :disabled="resetDisabled"
        @click="resetEmit"
      >
        {{ t(resetButton) }}
      </el-button>
    </div>
  </el-form>
</template>

<script lang="ts">
import { ElForm } from 'element-plus'
import { defineComponent, ref, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import FormControl from '@/components/form/common/FormControl.vue'
import { useClickLock } from '@/utils/useClickLock'

export default defineComponent({
  components: { FormControl },
  props: {
    formModel: {
      type: Object,
      default: () => {
        return null
      }
    },
    formStruct: {
      type: Array,
      default: () => []
    },
    formRules: {
      type: Object,
      default: () => {
        return null
      }
    },
    submitButton: {
      type: String,
      required: false,
      default: 'common.submit'
    },
    resetButton: {
      type: String,
      required: false,
      default: 'common.reset'
    },
    labelWidth: {
      type: String,
      required: false,
      default: '150px'
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    itemSpan: {
      type: Number,
      required: false,
      default: 7
    },
    itemOffset: {
      type: Number,
      required: false,
      default: 3
    },
    hadSlot: {
      type: Boolean,
      default: false
    },
    slotSpan: {
      type: Number,
      default: 2
    }
  },
  emits: ['submit', 'reset'],
  setup(props, context) {
    const { t } = useI18n()
    const rFormRef = ref(ElForm)
    const fM = ref(props.formModel)
    const fS = ref(props.formStruct)
    const fR = ref(props.formRules)
    const { disabled: submitDisabled, lock: submitLock } = useClickLock(2000)
    const { disabled: resetDisabled, lock: resetLock } = useClickLock(2000)
    const submitEmit = () => {
      rFormRef.value.validate((valid: any) => {
        if (valid) {
          submitLock(() => {
            context.emit('submit', toRaw(fM.value))
          })
        }
      })
    }
    const resetEmit = () => {
      resetLock(() => {
        (rFormRef.value as typeof ElForm).resetFields()
        context.emit('reset')
      })
    }
    return {
      t,
      fM,
      fS,
      fR,
      rFormRef,
      submitDisabled,
      resetDisabled,
      submitEmit,
      resetEmit
    }
  }
})
</script>
<style lang="scss">
.button-slot-wrapper {
  margin-left: 10px;
}
</style>
